import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  fetchAllNotes,
  fetchNoteById,
  addNote,
  updateNote,
  removeNote,
  removeNotesBulk,
} from '../actions/note.action'
import { INote } from '@dataTypes/note.type'

type NoteState = {
  data: INote[] | null
  currentNote: INote | null
  loading: boolean
  error: string | null
}

const initialState: NoteState = {
  data: null,
  currentNote: null,
  loading: false,
  error: null,
}

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    clearCurrentNote: (state) => {
      state.currentNote = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchAllNotes.fulfilled,
      (state, action: PayloadAction<INote[]>) => {
        state.data = action.payload
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(fetchAllNotes.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchAllNotes.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch notes'
      state.loading = false
    })

    builder.addCase(
      fetchNoteById.fulfilled,
      (state, action: PayloadAction<INote>) => {
        state.currentNote = action.payload
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(fetchNoteById.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchNoteById.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch note'
      state.loading = false
    })

    builder.addCase(
      addNote.fulfilled,
      (state, action: PayloadAction<INote>) => {
        state.data = state.data
          ? [...state.data, action.payload]
          : [action.payload]
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(addNote.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(addNote.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to create note'
      state.loading = false
    })

    builder.addCase(
      updateNote.fulfilled,
      (state, action: PayloadAction<INote>) => {
        const updatedNote = action.payload
        if (!state.data) return
        const index = state.data.findIndex(
          (item) => item.note_id === updatedNote.note_id,
        )
        if (index !== -1) {
          state.data[index] = updatedNote
        }
        if (state.currentNote?.note_id === updatedNote.note_id) {
          state.currentNote = updatedNote
        }
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(updateNote.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateNote.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update note'
      state.loading = false
    })

    builder.addCase(
      removeNote.fulfilled,
      (state, action: PayloadAction<string>) => {
        const noteId = action.payload
        if (!state.data) return
        const index = state.data.findIndex((item) => item.note_id === noteId)
        if (index !== -1) {
          state.data.splice(index, 1)
        }
        if (state.currentNote?.note_id === noteId) {
          state.currentNote = null
        }
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(removeNote.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(removeNote.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to delete note'
      state.loading = false
    })

    builder.addCase(
      removeNotesBulk.fulfilled,
      (state, action: PayloadAction<string[]>) => {
        const noteIds = action.payload
        if (!state.data) return
        state.data = state.data.filter(
          (item) => !noteIds.includes(item.note_id),
        )
        if (state.currentNote && noteIds.includes(state.currentNote.note_id)) {
          state.currentNote = null
        }
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(removeNotesBulk.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(removeNotesBulk.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to delete notes'
      state.loading = false
    })
  },
})

export const { clearCurrentNote } = notesSlice.actions
export default notesSlice.reducer
